import useFeature from '@core/components/LaunchDarkly';
import useDeepgramDirect from '@vh/utility/useDeepgramDirect';
import useDeepgramWithSDK from '@vh/utility/useDeepgramWithSDK';

/**
 * @typedef {import('./useDeepgram.types').UseDeepgramProps} UseDeepgramProps
 * @typedef {import('./useDeepgram.types').UseDeepgramReturn} UseDeepgramReturn
 * */

/**
 * React hook that switches between legacy direct socket connection and deepgram
 * sdk handling of speech to text
 * @type {(props: UseDeepgramProps) => UseDeepgramReturn}
 */
export const useDeepgram = ({ language, paused, onClose }) => {
  const deepgramSDKFeature = useFeature('use-deepgram-sdk');

  const {
    currentUnfinishedAffirmation: currentUnfinishedAffirmationDirect,
    currentAffirmation: currentAffirmationDirect,
    finalAffirmation: finalAffirmationDirect,
    error: errorDirect,
    connected: connectedDirect,
    reconnect: reconnectDirect,
    currentTranscript: currentTranscriptDirect,
    resetTranscript: resetTranscriptDirect,
    connecting: connectingDirect,
    closeSocket,
  } = useDeepgramDirect({
    language,
    paused: deepgramSDKFeature ? true : paused,
    onClose,
  });

  const {
    connected: connectedViaSDK,
    connecting: connectingViaSDK,
    error: errorViaSDK,
    currentAffirmation: currentAffirmationViaSDK,
    currentUnfinishedAffirmation: currentUnfinishedAffirmationViaSDK,
    currentTranscript: currentTranscriptViaSDK,
    finalAffirmation: finalAffirmationViaSDK,
    resetTranscript: resetTranscriptViaSDK,
    close: closeViaSDK,
    reconnect: reconnectViaSDK,
  } = useDeepgramWithSDK({
    language,
    paused: deepgramSDKFeature ? paused : true,
    onClose,
  });

  const currentUnfinishedAffirmation = deepgramSDKFeature
    ? currentUnfinishedAffirmationViaSDK
    : currentUnfinishedAffirmationDirect;
  const currentAffirmation = deepgramSDKFeature
    ? currentAffirmationViaSDK
    : currentAffirmationDirect;
  const finalAffirmation = deepgramSDKFeature
    ? finalAffirmationViaSDK
    : finalAffirmationDirect;
  const error = deepgramSDKFeature ? errorViaSDK : errorDirect;
  const connected = deepgramSDKFeature ? connectedViaSDK : connectedDirect;
  const reconnect = deepgramSDKFeature ? reconnectViaSDK : reconnectDirect;
  const currentTranscript = deepgramSDKFeature
    ? currentTranscriptViaSDK
    : currentTranscriptDirect;
  const resetTranscript = deepgramSDKFeature
    ? resetTranscriptViaSDK
    : resetTranscriptDirect;
  const connecting = deepgramSDKFeature ? connectingViaSDK : connectingDirect;

  return {
    currentUnfinishedAffirmation,
    currentAffirmation,
    finalAffirmation,
    error,
    connected,
    reconnect,
    currentTranscript,
    resetTranscript,
    connecting,
    closeSocket: deepgramSDKFeature ? closeViaSDK : closeSocket,
  };
};
